import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { defineMessages } from 'react-intl';

import { useClient } from '@/hooks/use-client';
import { useFeatures } from '@/hooks/use-features';
import { useScopeUrl } from '@/hooks/use-scope-url';
import { scopedQueryKey, useAppQueries, useAppQuery } from '@/queries/query';
import { useFilters } from '@/queries/settings/use-filters';
import { normalizeStatus, type NormalizedStatus } from '@/queries/statuses/normalize';
import { decrementReplyCount, incrementReplyCount } from '@/queries/statuses/status-actions';
import { trackStatusUpdates } from '@/queries/utils/fetch-statuses';
import { useImportEntities } from '@/queries/utils/import-entities';
import { useComposeActions } from '@/stores/compose';
import { getContextStatusIds, useContextsActions } from '@/stores/contexts';
import { useModalsActions } from '@/stores/modals';
import { usePendingStatusesActions } from '@/stores/pending-statuses';
import { setStatusUnavailable, isStatusDeleted } from '@/stores/status-availability';
import { useStatusMetaActions } from '@/stores/status-meta';
import { useTimelinesActions } from '@/stores/timelines';
import toast from '@/toast';
import { isUnavailableError } from '@/utils/errors';
import { checkFiltered } from '@/utils/filters';

import { useAccount } from '../accounts/use-account';
import { queryClient } from '../client';
import { queryKeys } from '../keys';

import { fetchStatusVerification, selectStatusVerifications } from './status-verification';

import type { Context, AsyncRefreshHeader, Account, Filter, FilterResult } from 'pl-api';

const messages = defineMessages({
  deleteSuccess: { id: 'compose.delete.success', defaultMessage: 'Post deleted' },
  deleteFail: { id: 'compose.delete.fail', defaultMessage: 'Failed to delete post' },
});

const minifyContext = ({
  ancestors,
  descendants,
  references,
  ...context
}: Context & { asyncRefreshHeader: AsyncRefreshHeader | null }) => ({
  ancestor_ids: ancestors.map(({ id }) => id),
  descendant_ids: descendants.map(({ id }) => id),
  reference_ids: references.map(({ id }) => id),
  ...context,
});

type MinifiedContext = ReturnType<typeof minifyContext>;

type SelectedStatus = NormalizedStatus & {
  account: Account;
  reblog: SelectedStatus | null;
  quote: SelectedStatus | null;
};

const useMinimalStatus = (statusId?: string) => {
  const client = useClient();
  const contextsActions = useContextsActions();
  const scope = useScopeUrl();
  const importEntities = useImportEntities();

  return useAppQuery({
    queryKey: queryKeys.statuses.show(statusId!),
    queryFn: async ({ signal }) => {
      const guard = trackStatusUpdates(scope, signal);
      const current = () =>
        isStatusDeleted(scope, statusId!) || !guard.isStatusCurrent(statusId!)
          ? queryClient.getQueryData(scopedQueryKey(queryKeys.statuses.show(statusId!), scope))
          : undefined;
      try {
        const status = await client.statuses.getStatus(statusId!);
        signal.throwIfAborted();
        guard.stop();
        const newer = current();
        if (newer) return newer;
        setStatusUnavailable(scope, statusId!, false);
        importEntities(
          { statuses: [status] },
          {
            withParents: false,
            isStatusCurrent: guard.isStatusCurrent,
            onStatusImported: guard.didImportStatus,
          },
        );
        contextsActions.importStatus(status);
        const normalized = normalizeStatus(status);
        guard.didImportStatus(status.id);
        return normalized;
      } catch (error) {
        signal.throwIfAborted();
        const newer = current();
        if (newer) return newer;
        if (isUnavailableError(error)) setStatusUnavailable(scope, statusId!, true);
        throw error;
      } finally {
        guard.stop();
      }
    },
    enabled: !!statusId,
  });
};

const useStatusQuery = (statusId?: string) => {
  const statusQuery = useMinimalStatus(statusId);

  const account = useAccount(statusQuery.data?.account_id ?? undefined);

  return useMemo(() => {
    if (!statusQuery.data) return statusQuery;
    return {
      ...statusQuery,
      data: {
        ...statusQuery.data,
        account: account.data!,
      },
    };
  }, [statusQuery, account.data]) as unknown as UseQueryResult<NormalizedStatus>;
};

const emptyFilters: Array<Filter> = [];
const emptyFilterResults: Array<FilterResult> = [];
const selectActiveFilters = (data: Array<Filter>) =>
  data.filter((filter) => filter.expires_at === null || Date.parse(filter.expires_at) > Date.now());
const selectNoFilters = () => emptyFilters;

const useStatus = (
  statusId?: string,
  {
    withContext,
    withFilteredResults,
  }: { withContext?: boolean; withFilteredResults?: boolean } = {},
) => {
  const features = useFeatures();
  const withClientSideFilters = !features.filtersV2 && withFilteredResults;

  const { data: filters } = useFilters(
    withClientSideFilters ? selectActiveFilters : selectNoFilters,
  );

  const { refetch: refetchContextQuery } = useStatusContext(withContext ? statusId : undefined);
  const refetchContext = useCallback(async () => {
    await refetchContextQuery({ throwOnError: true, cancelRefetch: false });
  }, [refetchContextQuery]);

  const statusQuery = useStatusQuery(statusId);

  // Shared by manual refresh and AsyncRefresh completion. Context alone does not
  // include the selected post, so edits and reply counts need a status refetch too.
  const refresh = useCallback(async () => {
    const [selected, context] = await Promise.allSettled([
      statusQuery.refetch({ throwOnError: true, cancelRefetch: false }),
      refetchContext(),
    ]);
    if (selected.status === 'rejected') {
      if (isUnavailableError(selected.reason)) {
        return;
      }
      throw selected.reason;
    }
    if (context.status === 'rejected') throw context.reason;
  }, [statusQuery.refetch, refetchContext]);

  const reblogQuery = useStatusQuery(statusQuery.data?.reblog_id ?? undefined);
  const quoteQuery = useStatusQuery(statusQuery.data?.quote_id ?? undefined);

  const clientFilterResults = useMemo(() => {
    if (!withClientSideFilters || !filters?.length || !statusQuery.data) return emptyFilterResults;
    return checkFiltered(statusQuery.data.search_index, filters);
  }, [withClientSideFilters, filters, statusQuery.data?.search_index]);

  return useMemo(() => {
    if (!statusQuery.data) return { ...statusQuery, refetchContext, refresh };

    const filtered = withClientSideFilters ? clientFilterResults : statusQuery.data.filtered;

    return {
      ...statusQuery,
      data: {
        ...statusQuery.data,
        reblog: reblogQuery.data ?? null,
        quote: quoteQuery.data ?? null,
        filtered,
      },
      refetchContext,
      refresh,
    };
  }, [
    statusQuery,
    refetchContext,
    refresh,
    reblogQuery.data,
    quoteQuery.data,
    clientFilterResults,
  ]) as unknown as UseQueryResult<SelectedStatus> & {
    refetchContext: () => Promise<void>;
    refresh: () => Promise<void>;
  };
};

const useStatusContext = (statusId?: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const scopeUrl = useScopeUrl();
  const { importContext, importStatus, setAsyncRefreshHeader, clearAsyncRefreshHeader } =
    useContextsActions();
  const importEntities = useImportEntities();

  return useAppQuery({
    queryKey: queryKeys.statuses.contexts(statusId!),
    queryFn: async ({ signal }) => {
      // Context may be limited or partial. Verify omitted, previously cached
      // ancestors/replies rather than treating absence as evidence of deletion.
      const previousStatuses = getContextStatusIds(statusId!).filter(
        (id) =>
          queryClient.getQueryData(scopedQueryKey(queryKeys.statuses.show(id), scopeUrl)) !==
          undefined,
      );
      const guard = trackStatusUpdates(scopeUrl, signal);
      let context;
      try {
        context = await client.statuses.getContext(statusId!);
      } finally {
        guard.stop();
      }
      signal.throwIfAborted();
      const { ancestors, descendants, references, asyncRefreshHeader } = context;
      const statuses = [...ancestors, ...descendants, ...references];
      importContext(statusId!, context);
      importEntities(
        { statuses },
        {
          withParents: true,
          isStatusCurrent: guard.isStatusCurrent,
          onStatusImported: guard.didImportStatus,
        },
      );

      if (asyncRefreshHeader) setAsyncRefreshHeader(statusId!, asyncRefreshHeader);
      else clearAsyncRefreshHeader(statusId!);

      const returned = new Set(statuses.map(({ id }) => id));
      const missing = previousStatuses.filter((id) => !returned.has(id));
      const candidates = selectStatusVerifications(client, queryClient, scopeUrl, missing);
      const queue = candidates.values();
      // Bound verification requests for large/partial threads and share in-flight
      // requests without allowing their obsolete results to overwrite newer data.
      await Promise.all(
        Array.from({ length: Math.min(4, candidates.length) }, async () => {
          for (const id of queue) {
            if (signal.aborted) return;
            const key = scopedQueryKey(queryKeys.statuses.show(id), scopeUrl);
            const original = queryClient.getQueryCache().find({ queryKey: key, exact: true });
            const revision = original?.state.dataUpdateCount;
            const isCurrent = () =>
              !signal.aborted &&
              !isStatusDeleted(scopeUrl, id) &&
              queryClient.getQueryCache().find({ queryKey: key, exact: true }) === original &&
              original?.state.dataUpdateCount === revision;
            try {
              const request = fetchStatusVerification(client, queryClient, scopeUrl, id);
              if (!request) continue;
              const status = await request;
              if (!isCurrent()) continue;
              importEntities({ statuses: [status] }, { withParents: false });
              queryClient.setQueryData(key, normalizeStatus(status));
              importStatus(status);
              setStatusUnavailable(scopeUrl, id, false);
            } catch (error) {
              if (isCurrent() && isUnavailableError(error))
                setStatusUnavailable(scopeUrl, id, true);
              // Transient and superseded responses preserve the current body.
            }
          }
        }),
      );
      signal.throwIfAborted();
      return minifyContext(context);
    },
    enabled: !!statusId,
  });
};

const useStatuses = (statusIds: Array<string>) => {
  const client = useClient();
  const importEntities = useImportEntities();
  const scope = useScopeUrl();

  return useAppQueries({
    queries: statusIds.map((id) => ({
      queryKey: queryKeys.statuses.show(id),
      queryFn: async ({ signal }) => {
        const guard = trackStatusUpdates(scope, signal);
        try {
          const status = await client.statuses.getStatus(id);
          signal.throwIfAborted();
          guard.stop();
          const newer =
            isStatusDeleted(scope, id) || !guard.isStatusCurrent(id)
              ? queryClient.getQueryData(scopedQueryKey(queryKeys.statuses.show(id), scope))
              : undefined;
          if (newer) return newer;
          importEntities(
            { statuses: [status] },
            {
              withParents: true,
              isStatusCurrent: guard.isStatusCurrent,
              onStatusImported: guard.didImportStatus,
            },
          );
          const normalized = normalizeStatus(status);
          guard.didImportStatus(status.id);
          return normalized;
        } finally {
          guard.stop();
        }
      },
      enabled: !!id,
    })),
  });
};

const findStatuses = (
  predicate: (status: NormalizedStatus) => boolean,
  scopeUrl?: string,
): Array<[string, NormalizedStatus]> =>
  queryClient
    .getQueriesData<NormalizedStatus>({
      predicate: ({ queryKey }) =>
        queryKey.length === 3 &&
        queryKey[1] === 'statuses' &&
        typeof queryKey[2] === 'string' &&
        (!scopeUrl || queryKey[0] === scopeUrl),
    })
    .filter(
      (entry): entry is [readonly [string, 'statuses', string], NormalizedStatus] =>
        entry[1] !== undefined && predicate(entry[1]),
    )
    .map(([key, data]) => [key[2], data]);

const useDeleteStatus = (statusId: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { markStatusDeleted } = useStatusMetaActions();
  const { deleteStatus: deletePendingStatus } = usePendingStatusesActions();
  const { deleteStatus: deleteStatusFromTimelines } = useTimelinesActions();
  const { setComposeToStatus } = useComposeActions();
  const { openModal } = useModalsActions();
  const scopeUrl = useScopeUrl();

  return useMutation({
    mutationFn: (_withRedraft?: boolean) => client.statuses.deleteStatus(statusId),
    onMutate: () => {
      const status = queryClient.getQueryData(
        scopedQueryKey(queryKeys.statuses.show(statusId), scopeUrl),
      );
      if (!status) return;

      decrementReplyCount(status, queryClient, scopeUrl);
    },
    onSuccess: (source, withRedraft) => {
      const status = queryClient.getQueryData(
        scopedQueryKey(queryKeys.statuses.show(statusId), scopeUrl),
      );

      const poll = status?.poll_id
        ? queryClient.getQueryData(
            scopedQueryKey(queryKeys.statuses.polls.show(status.poll_id), scopeUrl),
          )
        : undefined;

      deletePendingStatus(statusId);
      deleteStatusFromTimelines(scopeUrl, statusId);
      markStatusDeleted(statusId);

      if (withRedraft && status) {
        setComposeToStatus(status, poll, source, withRedraft);
        openModal('COMPOSE');
      }

      toast.success(messages.deleteSuccess);
    },
    onError: () => {
      const status = queryClient.getQueryData(
        scopedQueryKey(queryKeys.statuses.show(statusId), scopeUrl),
      );
      if (!status) return;

      incrementReplyCount(status, queryClient, scopeUrl);
      toast.error(messages.deleteFail);
    },
  });
};

const useDeleteStatusFromGroup = (statusId: string, groupId: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { markStatusDeleted } = useStatusMetaActions();
  const { deleteStatus: deletePendingStatus } = usePendingStatusesActions();
  const { deleteStatus: deleteStatusFromTimelines } = useTimelinesActions();
  const scopeUrl = useScopeUrl();

  return useMutation({
    mutationFn: () => client.experimental.groups.deleteGroupStatus(statusId, groupId),
    onMutate: () => {
      const status = queryClient.getQueryData(
        scopedQueryKey(queryKeys.statuses.show(statusId), scopeUrl),
      );
      if (!status) return;

      decrementReplyCount(status, queryClient, scopeUrl);
    },
    onSuccess: () => {
      deletePendingStatus(statusId);
      deleteStatusFromTimelines(scopeUrl, statusId);
      markStatusDeleted(statusId);
    },
    onError: () => {
      const status = queryClient.getQueryData(
        scopedQueryKey(queryKeys.statuses.show(statusId), scopeUrl),
      );
      if (!status) return;

      incrementReplyCount(status, queryClient, scopeUrl);
    },
  });
};

export {
  useMinimalStatus,
  useStatus,
  useStatusContext,
  useStatuses,
  useDeleteStatus,
  useDeleteStatusFromGroup,
  findStatuses,
  type MinifiedContext,
  type SelectedStatus,
};
